'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

import type { File } from '../../core/file-dropzone';
import { FileDropzone } from '../../core/file-dropzone';
import { FileIcon } from '../../core/file-icon';
import { getExtension, isPdf } from './file-storage-utils';
import { SetS3Config } from '../../../awsClientConfig';
import { userAccountData, userProfileData } from '../../../Common/profileInfo';
import { v4 as uuidv4 } from "uuid";
import { Storage } from 'aws-amplify';
import Loading from '../../../Common/Loading';

function bytesToSize(bytes: number, decimals = 2): string {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export interface UploaderProps {
  onUploaded: () => void;
  onClose:() => void;
  open?: boolean;
}

export function Uploader({ onUploaded, onClose, open = false }: UploaderProps): React.JSX.Element {
  const [files, setFiles] = React.useState<File[]>([]);

  React.useEffect(() => {
    setFiles([]);
  }, [open]);

  const handleDrop = React.useCallback((newFiles: File[]) => {
    setFiles((prevFiles) => {
      return [...prevFiles, ...newFiles];
    });
  }, []);

  const handleRemove = React.useCallback((file: File) => {
    setFiles((prevFiles) => {
      return prevFiles.filter((_file) => _file.path !== file.path);
    });
  }, []);

  const handleRemoveAll = React.useCallback(() => {
    setFiles([]);
  }, []);

  const uploadFilesHandler = async () => {
    for(let file of files){
      const extension = getExtension(file.name);
      const ispdf = isPdf(file.name);
      let contentType = ispdf ? "application/pdf" : "image/png";
      SetS3Config(
        `documents-${process.env.REACT_APP_MODE}-optima-financial-group`,
        "public"
      );

      const profileId = await userProfileData();
      const accountId = await userAccountData();
      const uuid = uuidv4();

      try {
        await Storage.put(
          `Accounts/${accountId}/Profiles/${profileId}/Identifications/${uuid}.${extension}`,
          file,
          {
            contentType: contentType, // contentType is optional
            customPrefix: { public: "" },
            tagging: "ExternalClearance=Profile",
          }
        ).then((res) => {
          console.log(res);
        });
      } catch (error) {
        console.log("Error uploading file: ", error);
      }
    }
    onUploaded();
  }

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography variant="h6">Upload files</Typography>
        <IconButton onClick={onClose}>
          <XIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        <Stack spacing={3}>
          <FileDropzone accept={{ '*/*': [] }} files={files} onDrop={handleDrop} />
          {files.length ? (
            <Stack spacing={2}>
              <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {files.map((file) => {
                  const extension = file.name.split('.').pop();

                  return (
                    <Stack
                      component="li"
                      direction="row"
                      key={file.path}
                      spacing={2}
                      sx={{
                        alignItems: 'center',
                        border: '1px solid var(--mui-palette-divider)',
                        borderRadius: 1,
                        flex: '1 1 auto',
                        p: 1,
                      }}
                    >
                      <FileIcon extension={extension} />
                      <Box sx={{ flex: '1 1 auto' }}>
                        <Typography variant="subtitle2">{file.name}</Typography>
                        <Typography color="text.secondary" variant="body2">
                          {bytesToSize(file.size)}
                        </Typography>
                      </Box>
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={() => {
                            handleRemove(file);
                          }}
                        >
                          <XIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button color="secondary" onClick={handleRemoveAll} size="small" type="button">
                  Remove all
                </Button>
                <Button onClick={uploadFilesHandler} size="small" type="button" variant="contained" className='option-primary-button'>
                  Upload
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
